import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  Form,
  Dropdown,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import AssignmentService, {
  Assignment,
} from "../../_services/AssignmentService";
import ApiService from "../../_services/ApiService";
import AssignmentFormModal from "./components/AssignmentFormModal";
import StudyResourceIssueModal from "../study-resources/components/StudyResourceIssueModal";
import "./assignments.css";

const RESPONSE_TYPE_LABELS: Record<string, string> = {
  TEXT: "Text",
  FILE: "File Upload",
  TEXT_AND_FILE: "Text + File",
  QUESTIONS: "Questions",
};

const AssignmentsListPage: React.FC = () => {
  const history = useHistory();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState<number | undefined>();
  const [courses, setCourses] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(
    null
  );
  const [issueAssignment, setIssueAssignment] = useState<Assignment | null>(
    null
  );
  const [deleteConfirm, setDeleteConfirm] = useState<Assignment | null>(null);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await AssignmentService.getAll({
        search: search || undefined,
        courseId: courseFilter,
        limit,
        offset,
      });
      setAssignments(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to load assignments", err);
    }
    setLoading(false);
  }, [search, courseFilter, offset]);

  const loadCourses = useCallback(async () => {
    try {
      const res = await ApiService.getAcademicCourses();
      setCourses(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load courses", err);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleCreate = () => {
    setEditingAssignment(null);
    setShowForm(true);
  };

  const handleView = async (a: Assignment) => {
    try {
      const res: any = await AssignmentService.getById(a.Id!);
      setEditingAssignment(res.data?.data || a);
    } catch {
      setEditingAssignment(a);
    }
    setFormReadOnly(true);
    setShowForm(true);
  };

  const handleEdit = async (a: Assignment) => {
    try {
      const res: any = await AssignmentService.getById(a.Id!);
      setEditingAssignment(res.data?.data || a);
      setShowForm(true);
    } catch {
      setEditingAssignment(a);
      setShowForm(true);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm?.Id) return;
    try {
      await AssignmentService.delete(deleteConfirm.Id);
      setDeleteConfirm(null);
      loadAssignments();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const handleFormClose = (refresh?: boolean) => {
    setShowForm(false);
    setEditingAssignment(null);
    setFormReadOnly(false);
    if (refresh) loadAssignments();
  };

  return (
    <div className="assignments-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Assignments</h4>
        <Button variant="primary" onClick={handleCreate}>
          + Create Assignment
        </Button>
      </div>

      {/* Filters */}
      <div className="d-flex gap-2 mb-3">
        <Form.Control
          type="text"
          placeholder="Search by title..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          style={{ maxWidth: 300 }}
        />
        <Form.Select
          value={courseFilter || ""}
          onChange={(e) => {
            setCourseFilter(e.target.value ? Number(e.target.value) : undefined);
            setOffset(0);
          }}
          style={{ maxWidth: 250 }}
        >
          <option value="">All Courses</option>
          {courses.map((c: any) => (
            <option key={c.Id || c.id || c.courseId} value={c.Id || c.id || c.courseId}>
              {c.Name || c.name || c.courseName}
            </option>
          ))}
        </Form.Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No assignments found. Click "Create Assignment" to get started.
        </div>
      ) : (
        <>
          <Table hover responsive className="assignments-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Response Type</th>
                <th>Course</th>
                <th>Max Score</th>
                <th>Resources</th>
                <th>Issued</th>
                <th>Created</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.Id}>
                  <td className="fw-medium">{a.Title}</td>
                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                        backgroundColor: "#e8f4fd",
                        color: "#1976d2",
                      }}
                    >
                      {RESPONSE_TYPE_LABELS[a.ResponseType] || a.ResponseType}
                    </span>
                  </td>
                  <td>{a.CourseName || "-"}</td>
                  <td>{a.MaxScore || "-"}</td>
                  <td>{a.ResourceCount || 0}</td>
                  <td>
                    {(a.IssueCount || 0) > 0 ? (
                      <span style={{ color: "#2e7d32" }}>
                        {a.IssueCount} batch{(a.IssueCount || 0) > 1 ? "es" : ""}
                      </span>
                    ) : (
                      <span className="text-muted">Not issued</span>
                    )}
                  </td>
                  <td>
                    {a.CreationDate
                      ? new Date(a.CreationDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="secondary"
                        size="sm"
                        id={`dd-${a.Id}`}
                      >
                        ...
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleView(a)}>
                          View
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleEdit(a)}>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setIssueAssignment(a)}
                          disabled={!a.StudyResourceId}
                        >
                          Issue to Batches
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            history.push(`/assignments/${a.Id}/submissions`)
                          }
                        >
                          View Submissions
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => setDeleteConfirm(a)}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {total > limit && (
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">
                Showing {offset + 1}–{Math.min(offset + limit, total)} of{" "}
                {total}
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <AssignmentFormModal
          show={showForm}
          onClose={handleFormClose}
          assignment={editingAssignment}
          readOnly={formReadOnly}
        />
      )}

      {/* Issue Modal — reuses existing study resource issue modal */}
      {issueAssignment && issueAssignment.StudyResourceId && (
        <StudyResourceIssueModal
          show={!!issueAssignment}
          onHide={() => setIssueAssignment(null)}
          resourceId={issueAssignment.StudyResourceId}
          resourceName={issueAssignment.Title}
        />
      )}

      {/* Delete Confirmation */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{deleteConfirm?.Title}"? This will
          also deactivate all issuances.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignmentsListPage;
